import dayjs from "dayjs";
import { PlayerMeasurementType, PlayerMeasurementUnit } from "@suns/api";
import {
  cn,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@suns/design-system";
import { formatInches } from "@/shared/utils/helper-functions";
import { percentileClassname } from "../utils";
import { ReactNode } from "react";

interface PlayerMeasurement {
  source: string;
  measurement: PlayerMeasurementType;
  unit: PlayerMeasurementUnit;
  value: number;
  measuredAt: string;
  percentile?: number;
}

const measurementColumns = [
  {
    type: PlayerMeasurementType.HEIGHT_W_SHOES,
    label: "Height",
  },
  {
    type: PlayerMeasurementType.WEIGHT,
    label: "Weight",
  },
  {
    type: PlayerMeasurementType.WINGSPAN,
    label: "Wingspan",
  },
  {
    type: PlayerMeasurementType.STANDING_REACH,
    label: "Standing Reach",
  },
  {
    type: PlayerMeasurementType.HAND_LENGTH,
    label: "Hand Length",
  },
  {
    type: PlayerMeasurementType.HAND_WIDTH,
    label: "Hand Width",
  },
  {
    type: PlayerMeasurementType.VERTICAL_JUMP_NO_STEP,
    label: "Standing Vertical",
  },
  {
    type: PlayerMeasurementType.VERTICAL_JUMP_MAX,
    label: "Max Vertical",
  },
  {
    type: PlayerMeasurementType.LANE_AGILITY,
    label: "Lane Agility",
  },
  {
    type: PlayerMeasurementType.RUN_THREE_FOUR,
    label: "3/4 Sprint",
  },
  {
    type: PlayerMeasurementType.SHUTTLE,
    label: "Shuttle Run",
  },
] satisfies {
  type: PlayerMeasurementType;
  label: string;
}[];

interface PlayerMeasurementProps {
  measurements: GroupedMeasurement[];
  caption?: ReactNode;
  hideSource?: boolean;
  hideMeasuredAt?: boolean;
  print?: boolean;
}

export function PlayerMeasurements({
  measurements,
  caption,
  hideSource = false,
  hideMeasuredAt = false,
  print = false,
}: PlayerMeasurementProps) {
  return (
    <Table
      className={cn(
        print && [
          "[&_td]:p-1",
          "[&_td]:text-2xs",
          "[&_th]:p-1",
          "[&_th]:text-2xs",
        ]
      )}
    >
      <TableHeader>
        <TableRow justify="left">
          {!hideMeasuredAt && <TableHead></TableHead>}
          {!hideSource && <TableHead>Source</TableHead>}
          {measurementColumns.map(({ type, label }) => (
            <TableHead key={`${type}-head`} className="font-bold">
              {label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody className="border">
        {measurements.map((group) => (
          <TableRow key={`${group.source}-${group.measuredAt}`} justify="left">
            {!hideMeasuredAt && (
              <TableCell className="text-nowrap">
                {dayjs(group.measuredAt).format("M/DD/YY")}
              </TableCell>
            )}
            {!hideSource && (
              <TableCell className="text-nowrap">{group.source}</TableCell>
            )}
            {measurementColumns.map(({ type, label }) => {
              const measurement = group.measurements[type];
              const className = percentileClassname(measurement?.percentile);

              return (
                <TableCell
                  className={cn("text-nowrap", className)}
                  key={`${group.source}-${group.measuredAt}-${label}`}
                >
                  {measurement
                    ? formatValue(measurement.value, measurement.unit)
                    : "--"}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
      {caption && (
        <TableCaption
          className={cn("text-left", print ? "text-3xs" : "text-xs")}
        >
          {caption}
        </TableCaption>
      )}
    </Table>
  );
}

interface GroupedMeasurement {
  source: PlayerMeasurement["source"];
  measuredAt: PlayerMeasurement["measuredAt"];
  measurements: Record<
    PlayerMeasurementType,
    Pick<PlayerMeasurement, "unit" | "value" | "percentile">
  >;
}

export function groupMeasurements(
  measurements: PlayerMeasurement[],
  flatten = false
): GroupedMeasurement[] {
  const newMeasurements = measurements.reduce<GroupedMeasurement[]>(
    (acc, measurement) => {
      const sourceName = measurement.source;
      if (acc.length == 0 && flatten) {
        acc.push({
          source: "Flattened",
          measuredAt: "",
          measurements: {} as GroupedMeasurement["measurements"],
        });
      }

      let groupToAddTo: GroupedMeasurement | undefined;
      if (flatten) {
        groupToAddTo = acc[0];
      } else {
        groupToAddTo = acc.find(
          ({ source, measuredAt }) =>
            source == sourceName && measurement.measuredAt == measuredAt
        );
      }

      if (groupToAddTo) {
        groupToAddTo.measurements[measurement.measurement] = {
          value: measurement.value,
          unit: measurement.unit,
          percentile: measurement.percentile,
        };
      } else {
        acc.push({
          source: sourceName,
          measuredAt: measurement.measuredAt,
          measurements: {
            [measurement.measurement]: {
              value: measurement.value,
              unit: measurement.unit,
              percentile: measurement.percentile,
            },
          } as GroupedMeasurement["measurements"],
        });
      }
      return acc;
    },
    []
  );

  newMeasurements.sort((a, b) => {
    return new Date(b.measuredAt).getTime() - new Date(a.measuredAt).getTime();
  });

  return newMeasurements.map(({ measurements, ...rest }) => {
    const wShoes = measurements[PlayerMeasurementType.HEIGHT_W_SHOES];
    const barefoot = measurements[PlayerMeasurementType.HEIGHT_BAREFOOT];

    if (wShoes && !barefoot) {
      measurements[PlayerMeasurementType.HEIGHT_BAREFOOT] = {
        ...wShoes,
        value: wShoes.value - 1.25,
      };
    } else if (barefoot && !wShoes) {
      measurements[PlayerMeasurementType.HEIGHT_W_SHOES] = {
        ...barefoot,
        value: barefoot.value + 1.25,
      };
    }

    return { measurements, ...rest };
  });
}

const formatUnit = (unit: PlayerMeasurementUnit): string => {
  switch (unit) {
    case PlayerMeasurementUnit.SECONDS:
      return "s";
    case PlayerMeasurementUnit.PERCENT:
      return "%";
    case PlayerMeasurementUnit.REPS:
      return "rep(s)";
    case PlayerMeasurementUnit.LBS:
      return "lbs";
    default:
      return "";
  }
};

function formatValue(value: number, unit: PlayerMeasurementUnit) {
  const formattedValue =
    unit == PlayerMeasurementUnit.INCHES ? formatInches(value) : value;
  return `${formattedValue} ${formatUnit(unit)}`;
}
